import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { signInWithEmail, signInWithGoogle } from '../../services/authService';
import { FirebaseError } from 'firebase/app';
import GoogleIcon from '../../assets/google-icon.svg';
import './Auth.css';

interface SignInProps {
  onClose: () => void;
  onSwitchToSignUp: () => void;
  onSwitchToForgotPassword: () => void;
  onSwitchToForgotUsername: () => void;
}

export default function SignIn({ onClose, onSwitchToSignUp, onSwitchToForgotPassword, onSwitchToForgotUsername }: SignInProps) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('auth.errors.invalidEmail') || 'Invalid email address')
      .required(t('auth.errors.emailRequired') || 'Email is required'),
    password: Yup.string()
      .required(t('auth.errors.passwordRequired') || 'Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setError('');
      setLoading(true);

      try {
        await signInWithEmail(values.email, values.password);
        onClose();
      } catch (err) {
        console.error('Sign in failed:', err);
        if (err instanceof FirebaseError) {
          console.error('Firebase error code:', err.code);
          console.error('Firebase error message:', err.message);
          switch (err.code) {
            case 'auth/invalid-email':
              setError(t('auth.errors.invalidEmail'));
              break;
            case 'auth/user-disabled':
              setError(t('auth.errors.userDisabled'));
              break;
            case 'auth/user-not-found':
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
              setError(t('auth.errors.invalidCredentials'));
              break;
            default:
              setError(t('auth.errors.signInFailed'));
          }
        } else {
          console.error('Non-Firebase error:', err);
          setError(t('auth.errors.signInFailed'));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
      console.log('Google sign in successful');
      onClose();
    } catch (err) {
      console.error('Google sign in failed:', err);
      if (err instanceof FirebaseError) {
        console.error('Firebase error code:', err.code);
        console.error('Firebase error message:', err.message);
        switch (err.code) {
          case 'auth/popup-closed-by-user':
            setError(t('auth.errors.popupClosed'));
            break;
          case 'auth/popup-blocked':
            setError(t('auth.errors.popupBlocked'));
            break;
          default:
            setError(t('auth.errors.googleSignInFailed'));
        }
      } else {
        console.error('Non-Firebase error:', err);
        setError(t('auth.errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form">
      {error && <div className="auth-error">{error}</div>}

      <form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">{t('auth.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.emailPlaceholder')}
          />
          {formik.touched.email && formik.errors.email && (
            <span className="error-message">{formik.errors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('auth.password')}</label>
          <input
            type="password"
            id="password"
            name="password"
            autoComplete="current-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.passwordPlaceholder')}
          />
          {formik.touched.password && formik.errors.password && (
            <span className="error-message">{formik.errors.password}</span>
          )}
        </div>

        <div className="auth-links">
          <button type="button" className="auth-link" onClick={onSwitchToForgotPassword}>
            {t('auth.forgotPassword')}
          </button>
          <button type="button" className="auth-link" onClick={onSwitchToForgotUsername}>
            {t('auth.forgotUsername')}
          </button>
        </div>

        <button type="submit" className="auth-submit-button" disabled={loading}>
          {loading ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </form>

      <div className="auth-divider">
        <span>{t('auth.orContinueWith')}</span>
      </div>

      <button
        type="button"
        className="auth-google-button"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        <img src={GoogleIcon} alt="Google" className="google-icon" />
        {t('auth.continueWithGoogle')}
      </button>

      <div className="auth-footer">
        <p>
          {t('auth.noAccount')}{' '}
          <button type="button" className="auth-link" onClick={onSwitchToSignUp}>
            {t('auth.signUp')}
          </button>
        </p>
      </div>
    </div>
  );
}
